// ─────────────────────────────────────────────────────────────
// Riden Nexus API client (browser)
//
// Talks to the same FastAPI backend that powers the admin console and
// the mobile apps. Configure the base URL with REACT_APP_API_BASE
// (defaults to the local dev server). The passenger JWT is stored in
// localStorage and attached to authenticated calls.
// ─────────────────────────────────────────────────────────────

export const API_BASE = (
  process.env.REACT_APP_API_BASE || 'http://localhost:8000'
).replace(/\/+$/, '');

const TOKEN_KEY = 'riden_token';
const USER_KEY = 'riden_user';

// ── Session ──────────────────────────────────────────────────
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
};
export const isAuthed = () => Boolean(getToken());
export const setSession = (token, user) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// ── Core request helper ──────────────────────────────────────
async function request(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError(
      `Can't reach the Riden API at ${API_BASE}. Is the backend running?`,
      0,
    );
  }

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const detail =
      (data && (data.detail || data.message)) ||
      (typeof data === 'string' && data) ||
      `Request failed (${res.status})`;
    throw new ApiError(
      Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : detail,
      res.status,
      data,
    );
  }
  return data;
}

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// ── Geography ────────────────────────────────────────────────
export const getMarkets = () => request('/markets');

// Pick the market whose centre is closest to a point (haversine-ish).
export function nearestMarket(markets, lat, lng) {
  if (!markets || !markets.length) return null;
  const d2 = (m) =>
    (m.center_lat - lat) ** 2 + (m.center_lng - lng) ** 2;
  return [...markets].sort((a, b) => d2(a) - d2(b))[0];
}

// ── Fares / catalogue ────────────────────────────────────────
export const getCarTypes = (marketId) =>
  request(`/fares/car-types/${marketId}`);

export const estimateFare = ({
  marketId,
  vehicleClass,
  pickup,
  dropoff,
  promoCode,
}) =>
  request('/fares/estimate', {
    method: 'POST',
    body: {
      market_id: marketId,
      vehicle_class: vehicleClass,
      pickup_lat: pickup.lat,
      pickup_lng: pickup.lng,
      dropoff_lat: dropoff.lat,
      dropoff_lng: dropoff.lng,
      promo_code: promoCode || null,
    },
  });

// ── Auth (passenger OTP) ─────────────────────────────────────
export const requestOtp = (phone) =>
  request('/auth/otp/request', { method: 'POST', body: { phone } });

export async function verifyOtp(phone, code, fullName) {
  const data = await request('/auth/otp/verify', {
    method: 'POST',
    body: { phone, code, full_name: fullName || null },
  });
  setSession(data.access_token, {
    id: data.user_id,
    role: data.role,
    phone,
    full_name: fullName || null,
  });
  return data;
}

// ── Rides ────────────────────────────────────────────────────
export const createRide = ({
  marketId,
  vehicleClass,
  pickup,
  dropoff,
  promoCode,
}) =>
  request('/rides', {
    method: 'POST',
    auth: true,
    body: {
      market_id: marketId,
      vehicle_class: vehicleClass,
      pickup_lat: pickup.lat,
      pickup_lng: pickup.lng,
      pickup_address: pickup.address || '',
      dropoff_lat: dropoff.lat,
      dropoff_lng: dropoff.lng,
      dropoff_address: dropoff.address || '',
      promo_code: promoCode || null,
    },
  });

export const getRide = (rideId) =>
  request(`/rides/${rideId}`, { auth: true });

export const cancelRide = (rideId, reason = 'passenger_cancelled') =>
  request(`/rides/${rideId}/cancel`, {
    method: 'POST',
    auth: true,
    body: { reason },
  });

// ── Geocoding (OpenStreetMap Nominatim — no key, biased to Canada) ──
export async function geocode(query) {
  if (!query || !query.trim()) return null;
  const url =
    'https://nominatim.openstreetmap.org/search?format=json&limit=1' +
    '&countrycodes=ca&q=' +
    encodeURIComponent(query.trim());
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const rows = await res.json();
    if (!rows || !rows.length) return null;
    const r = rows[0];
    return {
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      address: r.display_name,
    };
  } catch {
    return null;
  }
}

const RidenApi = {
  API_BASE,
  getMarkets,
  nearestMarket,
  getCarTypes,
  estimateFare,
  requestOtp,
  verifyOtp,
  createRide,
  getRide,
  cancelRide,
  geocode,
  getToken,
  getUser,
  isAuthed,
  setSession,
  clearSession,
};

export default RidenApi;
